import { Grid, Typography, Switch, FormControlLabel } from "@mui/material";
import ListEmails from "../../components/tables/ListEmails";
import { useContext, useEffect, useState } from "react";
import EmailCustomizationModal from "../../components/modals/EmailCustomizationModal";
import { IEmailTemplate, IMerchantToggleConfig } from "../../interfaces/order";
import {
  getEmailTemplateList,
  getMerchantToggleConfig,
  updateEmailDisabledConfiguration,
  updateMerchantToggleConfig,
} from "../../requests/orderRequests";
import { IAlert } from "../../interfaces/alert";
import { ERROR, MARKETPLACE } from "../../utils/constants";
import { filterMachineValidTemplates } from "../../utils/emailTemplates";
import GenericAlert from "../../components/GenericAlert";
import Loader from "../../components/Loader";
import { IDynamicObjectReturnBoolean } from "../../interfaces/common";
import ConfirmChangesButtons from "../../components/configuration/ConfirmChangesButtons";
import { UserContext } from "../../contexts/userContext";

const EmailCustomization = () => {
  const { currentUser: { entity } } = useContext(UserContext);
  const [openModal, setOpenModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState("");
  const [selectedStatus, setSelectedStatus] = useState({} as IEmailTemplate);
  const [emailTemplates, setEmailTemplates] = useState<IEmailTemplate[]>([]);
  const [alert, setAlert] = useState({} as IAlert);
  const [loading, setLoading] = useState(true);
  const [toggledDisabledStatuses, setToggledDisabledStatuses] = useState(
    {} as IDynamicObjectReturnBoolean
  );
  const [merchantToggleEnabled, setMerchantToggleEnabled] = useState<boolean | undefined>(undefined);
  const [pendingMerchantToggle, setPendingMerchantToggle] = useState<
    boolean | undefined
  >(undefined);

  const handleSelectedAction = (action: string, status: IEmailTemplate) => {
    setOpenModal(true);
    setSelectedAction(action);
    setSelectedStatus(status);
  };

  const handleToggleDisableStatus = (statusId: string, checked: boolean) =>
    setToggledDisabledStatuses((prev) => ({ ...prev, [statusId]: !checked }));

  const fetchAll = async () => {
    setLoading(true);
    try {
      const isMarketplace = entity === MARKETPLACE;
      const [templatesResponse, toggleResponse] = await Promise.all([
        getEmailTemplateList() as Promise<Record<string, IEmailTemplate>>,
        isMarketplace ? getMerchantToggleConfig() as Promise<IMerchantToggleConfig> : Promise.resolve(undefined),
      ]);
      if (templatesResponse.footer) delete templatesResponse.footer;
      setEmailTemplates(
        filterMachineValidTemplates(Object.values(templatesResponse) as IEmailTemplate[]),
      );
      if (toggleResponse) setMerchantToggleEnabled(toggleResponse.enabledMerchantToggle ?? false);
      setAlert({} as IAlert);
    } catch (error: any) {
      setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDisabledStatusConfiguration = async () => {
    try {
      const requests: Promise<unknown>[] = [];
      if (Object.keys(toggledDisabledStatuses).length > 0)
        requests.push(updateEmailDisabledConfiguration(toggledDisabledStatuses));
      if (pendingMerchantToggle !== undefined)
        requests.push(updateMerchantToggleConfig(pendingMerchantToggle));
      await Promise.all(requests);
      setToggledDisabledStatuses({} as IDynamicObjectReturnBoolean);
      setPendingMerchantToggle(undefined);
      await fetchAll();
    } catch (error: any) {
      setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
    }
  };

  const handleCancel = () => {
    setToggledDisabledStatuses({} as IDynamicObjectReturnBoolean);
    setPendingMerchantToggle(undefined);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const hasPendingChanges =
    Object.keys(toggledDisabledStatuses).length > 0 ||
    pendingMerchantToggle !== undefined;

  return (
    <>
      <Typography fontSize={20} mb={2}>
        Emails transaccionales
      </Typography>
      <Typography fontSize={14} mb={3.5}>
        Selecciona y personaliza las notificaciones por correo para los estados
        logísticos que desees.
      </Typography>
      {alert.showAlert && (
        <Grid item xs={12} mb={3}>
          <GenericAlert alert={alert} />
        </Grid>
      )}
      <Grid item xs={12} lg={8}>
        {loading ? (
          <Loader size="md" />
        ) : (
          <>
            {entity === MARKETPLACE && (
              <Grid item xs={12} mb={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={pendingMerchantToggle ?? merchantToggleEnabled ?? false}
                      disabled={merchantToggleEnabled === undefined}
                      onChange={(_, checked) => setPendingMerchantToggle(checked)}
                    />
                  }
                  label={
                    <Typography fontSize={14}>
                      Permitir que los sellers personalicen notificaciones de email
                    </Typography>
                  }
                />
              </Grid>
            )}
            <ListEmails
              emailTemplates={emailTemplates}
              handleSelectedAction={handleSelectedAction}
              toggledDisabledStatuses={toggledDisabledStatuses}
              handleToggleDisableStatus={handleToggleDisableStatus}
            />
          </>
        )}
        {hasPendingChanges && (
          <ConfirmChangesButtons
            confirmButtonText="Guardar cambios"
            handleCancel={handleCancel}
            handleConfirm={handleUpdateDisabledStatusConfiguration}
            size={2.5}
          />
        )}
      </Grid>

      <EmailCustomizationModal
        open={openModal}
        setOpen={setOpenModal}
        action={selectedAction}
        status={selectedStatus}
        emailTemplates={emailTemplates}
        fetchEmailTemplates={fetchAll}
      />
    </>
  );
};

export default EmailCustomization;
